import React, { FC, ReactNode } from 'react';

import { ThemeProvider as EmotionReactThemeProvider } from '@emotion/react';
import {
  theme as defaultTheme,
  ThemeProps,
  Products
} from '@lucid/theme';
import get from 'lodash/get';
import merge from 'lodash/merge';
import omit from 'lodash/omit';

export interface ThemeProviderSiteThemeProps {
  theme: ThemeProps['theme'];
  product: Products;
}

export interface ThemeProviderProps
  extends ThemeProviderSiteThemeProps {
  children?: ReactNode | ReactNode[];
}

const getTheme = (
  {
    product,
    theme
  }: ThemeProviderSiteThemeProps
) => {
  const mutatedTheme = merge({}, theme, {
    accordion: {
      button: get(theme!.accordion!.button.products, product, theme!.accordion!.button),
      heading: get(theme!.accordion!.heading.products, product, theme!.accordion!.heading),
      item: get(theme!.accordion!.item.products, product, theme!.accordion!.item),
      panel: get(theme!.accordion!.panel.products, product, theme!.accordion!.panel),
    },
    buttonSizes: get(theme!.buttonSizes!.products, product, theme!.buttonSizes),
    buttons: get(theme!.buttons!.products, product, theme!.buttons),
    cards: {
      card: get(theme!.cards!.card!.products, product, theme!.cards!.card),
      carousel: get(theme!.cards!.carousel!.products, product, theme!.cards!.carousel),
    },
    colors: get(theme!.colors!.products, product, theme!.colors),
    featureGrid: {
      container: get(theme!.featureGrid!.container.products, product, theme!.featureGrid.container),
      heading: get(theme!.featureGrid!.heading.products, product, theme!.featureGrid!.heading),
    },
    fontSizes: get(theme!.fontSizes!.products, product, theme!.fontSizes),
    form: {
      dropdown: get(theme!.form!.dropdown!.products, product, theme!.form!.dropdown),
      input: get(theme!.form!.input!.products, product, theme!.form!.input)
    },
    headings: get(theme!.headings!.products, product, theme!.headings),
    hero: {
      carouselSlider: get(theme!.hero!.carouselSlider.products, product, theme!.hero.carouselSlider),
    },
    links: get(theme!.links!.products, product, theme!.links),
    logoSection: {
      layouts: get(theme!.logoSection!.layouts!.products, product, theme!.logoSection!.layouts),
      colors: get(theme!.logoSection!.colors!.products, product, theme!.logoSection!.colors)
    },
    modal: get(theme!.modal!.products, product, theme!.modal),
    nav: {
      logo: get(theme!.nav!.logo.products, product, theme!.nav.logo)
    },
    paper: get(theme!.paper!.products, product, theme!.paper),
    radii: get(theme!.radii!.products, product, theme!.radii),
    shadows: get(theme!.shadows!.products, product, theme!.shadows),
    typography: get(theme!.typography!.products, product, theme!.typography),
    videoCarousel: get(theme!.videoCarousel!.products, product, theme!.videoCarousel),
    videoPlayer: get(theme!.videoPlayer!.products, product, theme!.videoPlayer),
    wave: get(theme!.wave!.products, product, theme!.wave),
  });

  const finalTheme = omit(
    mutatedTheme,
    [
      'accordion.button.products',
      'accordion.heading.products',
      'accordion.item.products',
      'accordion.panel.products',
      'buttonSizes.products',
      'buttons.products',
      'cards.card.products',
      'cards.carousel.products',
      'colors.products',
      'featureGrid.container.products',
      'featureGrid.heading.products',
      'fontSizes.products',
      'form.dropdown.products',
      'form.input.products',
      'headings.products',
      'hero.carouselSlider.products',
      'links.products',
      'logoSection.layouts.products',
      'logoSection.colors.products',
      'modal.products',
      'nav.logo.products',
      'paper.products',
      'radii.products',
      'shadows.products',
      'typography.products',
      'videoCarousel.products',
      'videoPlayer.products',
      'wave.products'
    ]
  );

  return finalTheme;
};

const ThemeProvider: FC<ThemeProviderProps> = (
  {
    children,
    theme = defaultTheme,
    product
  }
) => {
  const siteSpecificTheme = getTheme({ product, theme });

  return (
    <EmotionReactThemeProvider theme={siteSpecificTheme || theme}>
      {children}
    </EmotionReactThemeProvider>
  );
};

export { ThemeProvider };
export default ThemeProvider;
